import type { ReactNode } from 'react';
import { useCustomerCompany, type CustomerCompanyState } from '../../api/customerCompanies.ts';
import { useT } from '../../i18n/index.ts';
import { cx } from '../../ui/cx.ts';
import { CustomerRef } from './CustomerRef.tsx';

function companyLineHasId(orgId: string | null | undefined, accountId: string | null | undefined): boolean {
  return (orgId?.trim() ?? '').length > 0 || (accountId?.trim() ?? '').length > 0;
}

/**
 * Resolved company line. Idle, loading, a failed lookup, and a missing
 * id render nothing so a caller never flashes "(not filled)" early.
 */
function CompanyLineBody({
  company,
  hasId,
  className,
  testId,
}: {
  readonly company: CustomerCompanyState;
  readonly hasId: boolean;
  readonly className?: string;
  readonly testId?: string;
}): JSX.Element | null {
  const t = useT();
  if (!hasId || company.status !== 'resolved') {
    return null;
  }
  if (company.company !== null) {
    return (
      <span
        className={cx('truncate text-dense text-paper', className)}
        title={company.company}
        data-testid={testId}
        data-customer-company-status={company.status}
      >
        {company.company}
      </span>
    );
  }
  return (
    <span
      className={cx('text-dense text-paper-low', className)}
      data-testid={testId}
      data-customer-company-status={company.status}
    >
      {t('customer.company.missing')}
    </span>
  );
}

/**
 * Company line alone. Idle, loading, a failed lookup, and a missing id
 * render nothing so a caller never flashes "(not filled)" before the answer.
 */
export function CustomerCompanyLine({
  orgId,
  accountId,
  className,
  testId,
}: {
  readonly orgId?: string | null;
  readonly accountId?: string | null;
  readonly className?: string;
  readonly testId?: string;
}): JSX.Element | null {
  const company = useCustomerCompany({ orgId, accountId });
  return (
    <CompanyLineBody
      company={company}
      hasId={companyLineHasId(orgId, accountId)}
      className={className}
      testId={testId}
    />
  );
}

/**
 * Stacked customer cell: CRM company on the first line, the stable
 * identity (a `CustomerRef`, or a caller-supplied secondary) underneath.
 * Idle, loading, and a failed lookup omit the first line so a cell never
 * flashes "(not filled)" before the answer and never shows it on failure.
 */
export function CustomerCell({
  orgId,
  accountId,
  secondary,
  testId,
  className,
}: {
  readonly orgId?: string | null;
  readonly accountId?: string | null;
  readonly secondary?: ReactNode;
  readonly testId?: string;
  readonly className?: string;
}): JSX.Element {
  const t = useT();
  const company = useCustomerCompany({ orgId, accountId });
  const trimmedOrg = orgId?.trim() ?? '';
  const trimmedAccount = accountId?.trim() ?? '';
  const hasId = trimmedOrg.length > 0 || trimmedAccount.length > 0;

  const wrapperClass = cx('flex min-w-0 flex-col gap-0.5', className);

  if (!hasId) {
    return (
      <div className={wrapperClass} data-testid={testId} data-customer-company-status="idle">
        {secondary !== undefined ? secondary : t('credit.overview.dash')}
      </div>
    );
  }

  const line2 =
    secondary === undefined ? (
      <CustomerRef
        orgId={trimmedOrg.length > 0 ? trimmedOrg : null}
        accountId={trimmedAccount}
        withCompany={false}
      />
    ) : typeof secondary === 'string' ? (
      <span className="font-mono text-micro text-paper-low">{secondary}</span>
    ) : (
      secondary
    );

  return (
    <div
      className={wrapperClass}
      data-testid={testId}
      data-customer-company-status={company.status}
    >
      <CompanyLineBody company={company} hasId={hasId} />
      {line2}
    </div>
  );
}
